import _ from 'lodash'
import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { deleteCartItem, updateCartItem, clearSweetAlert, dispatchSweetAlert, fetchCartItems } from '../../actions'
import { setErrors, setMaxQuantityLoading, setCheckoutLoading } from '../Checkout/checkoutFunctions'
import ContentLoader from '../global/loaders/InlineContentLoader'
import { stockErrors } from './cartFunctions'
import CartItemCard from './CartItemCard'

const CartItemsList = props => {
  const dispatch = useDispatch()

  // Redux state
  const currentLocation = useSelector(state => state.storeViews.current)
  const portal = useSelector(state => state.portal)
  const customerGroup = useSelector(state => state.customerGroup)
  const reduxCartItems = useSelector(state => state.cartItems)
  const cart_items_loading = useSelector(state => state.productPage.cart_items_loading)
  const maxQuantities = useSelector(state => state.maxQuantities)
  const errors = useSelector(state => state.checkout.errors)
  const youPay = useSelector(state => state.checkout.youPay)
  const checkout = useSelector(state => state.checkout)
  const userCredits = useSelector(state => state.userCredits)
  const maxQuantityLoading = useSelector(state => state.checkout.maxQuantityLoading)
  const currentUser = useSelector(state => state.currentUser)

  // Use filteredCartItems if provided (for shipping groups), otherwise use Redux cartItems
  const cartItems = props.filteredCartItems || reduxCartItems

  const [deleteConfirmationItem, setDeleteConfirmationItem] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState({})
  const [maxQtyAnyProduct, setMaxQtyAnyProduct] = useState(false)
  const [maxQtyProductReached, setMaxQtyProductReached] = useState({})
  const [query, setQuery] = useState({ cartItemId: null, qty: null })
  const [queryLoading, setQueryLoading] = useState(false)
  const [cartItemQuantities, setCartItemQuantities] = useState({})

  // set checkout error if query is loading to prevent moving between pages
  useEffect(() => {
    if (queryLoading) {
      dispatch(setCheckoutLoading(true))
    } else {
      dispatch(setCheckoutLoading(false))
    }
  }, [queryLoading, dispatch])

  useEffect(() => {
    if (query.cartItemId) {
      // runs only if query has id - prevents initial setting of true
      setQueryLoading(true)
      const timeOutId = setTimeout(() => {
        updateCartItemQuantity()
      }, 400)
      return () => clearTimeout(timeOutId)
    }
  }, [query])

  useEffect(() => {
    resolveProductQuantities()
  }, [cartItems])

  useEffect(() => {
    if (cartItems.length !== Object.keys(cartItemQuantities).length) {
      const cartItemsMappedToIds = _.mapKeys(cartItems, 'cart_item_id')
      const cartItemsMappedToQty = _.mapValues(cartItemsMappedToIds, el => el.quantity)
      setCartItemQuantities(cartItemsMappedToQty)
    }
  }, [cartItems])

  useEffect(() => {
    // check cart items for stock errors
    const stockError = stockErrors(cartItems)

    if (!_.isEqual(stockError, errors.stock)) {
      dispatch(setErrors({ stock: stockError }))
    }
  }, [cartItems, deleteConfirmationItem, dispatch])

  useEffect(() => {
    const hasMaxQtyError = Object.values(maxQtyProductReached).some(val => val === true)

    if (hasMaxQtyError) {
      setMaxQtyAnyProduct(true)
      // Only dispatch if the error state has changed
      if (errors.maxQtyReached !== true) {
        dispatch(setErrors({ maxQtyReached: true }))
      }
    } else {
      setMaxQtyAnyProduct(false)
      // Only dispatch if the error state has changed
      if (errors.maxQtyReached !== false) {
        dispatch(setErrors({ maxQtyReached: false }))
      }
    }
  }, [maxQtyProductReached, dispatch, errors.maxQtyReached])

  // componentDidMount
  useEffect(() => {
    maxQtyProductHandler()

    if (errors.stock && !props.mini) {
      dispatch(dispatchSweetAlert({
        type: 'error',
        alertMessage: 'Some items are out of stock! Please remove the highlighted items from your basket to continue.',
        showCancel: false,
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn'
      }))
    }
  }, [])

  useEffect(() => {
    if (!_.isEmpty(cartItemQuantities) && !_.isEmpty(maxQuantities)) {
      maxQtyProductHandler()
    }
  }, [maxQuantities, cartItemQuantities])

  useEffect(() => {
    if (maxQtyAnyProduct === true && !props.mini) {
      toast.error('You have exceeded the maximum allowed quantity for a product.')
    }
  }, [maxQtyAnyProduct])

  // when item is selected for deletion, dispatch sweetalert
  useEffect(() => {
    if (!!deleteConfirmationItem && Object.keys(deleteConfirmationItem).length > 0) {
      handleDeleteCartItem()
    }
  }, [deleteConfirmationItem])

  // Helper functions

  const resolveProductQuantities = () => {
    if (!props.mini) {
      // reset qty value for each cart item after fetching, since qty is not controlled
      cartItems.forEach(cartItem => {
        // if there is a discrepancy between displayed value and DB value
        if (cartItem.quantity !== cartItemQuantities[cartItem.cart_item_id]) {
          // set value
          setCartItemQuantities({ ...cartItemQuantities, [cartItem.cart_item_id]: cartItem.quantity })
        }
      })
    }
  }

  const deleteCartItemAction = () => {
    setQueryLoading(true)
    dispatch(deleteCartItem(deleteConfirmationItem.cart_item_id)).then(() => {
      fetchCartItemsAction()
      setDeleteConfirmationItem(false)
    })
    dispatch(clearSweetAlert())
  }

  const resetDeleteSelection = () => {
    setDeleteConfirmationItem(false)
    dispatch(clearSweetAlert())
    setQueryLoading(false)
  }

  const handleDeleteCartItem = () => {
    const propsForSweetalert = {
      type: 'warning',
      onConfirm: deleteCartItemAction,
      onCancel: resetDeleteSelection,
      confirmBtnText: 'Remove',
      cancelBtnText: 'Do Not Remove',
      confirmBtnCssClass: 'mf-primary-btn alert-width-btn',
      cancelBtnCssClass: 'mf-outline-btn alert-width-btn',
      alertMessage: `Remove this item from your cart?` + ` ${deleteConfirmationItem.name}`
    }
    dispatch(dispatchSweetAlert(propsForSweetalert))
  }

  const updateCartItemQuantity = () => {
    const cartItemId = query.cartItemId
    const newQuantity = query.qty

    const products = _.mapKeys(cartItems, 'cart_item_id')
    let quantity = parseInt(newQuantity)

    // if new qty is < 1, ask user to delete
    if (!quantity || quantity < 1) {
      setDeleteConfirmationItem(products[cartItemId])
      return
    }

    const thisProduct = products[cartItemId]

    // check if minimum quantity is met
    if (thisProduct && quantity < thisProduct.minimum_qty_allowed_in_shopping_cart) {
      // Set error if min qty is not met
      dispatch(setErrors({
        ...errors,
        minQtyError: [...(errors.minQtyError || []), cartItemId]
      }))
    } else {
      // Remove item ID from errors array if min qty IS met
      dispatch(setErrors({
        ...errors,
        minQtyError: _.reject(errors.minQtyError, e => e.cart_item_id === cartItemId)
      }))
    }
    // end min qty check

    // check for qty increments
    if (thisProduct && thisProduct.enable_qty_increments) {
      // if product comes in increments, validate the incoming quantity
      if (quantity % thisProduct.qty_increments !== 0) {
        quantity = Math.ceil(quantity / thisProduct.qty_increments) * thisProduct.qty_increments
        dispatch(setErrors({
          ...errors,
          incrQtyError: [...errors.incrQtyError, cartItemId]
        }))
      } else {
        dispatch(setErrors({
          ...errors,
          incrQtyError: []
        }))
      }
    }
    // end qty increment check

    dispatch(updateCartItem(cartItemId, quantity)).then(() => {
      maxQtyProductHandler()
      fetchCartItemsAction()
    })
  }

  const fetchCartItemsAction = () => {
    dispatch(fetchCartItems()).then(() => {
      setQueryLoading(false)
    })
  }

  const maxQtyProductHandler = () => {
    // unify all products and map by product_id
    const products_unified = {}
    cartItems.map(product => {
      if (products_unified[product.product_id]) {
        products_unified[product.product_id] += product.quantity
      } else {
        products_unified[product.product_id] = product.quantity
      }
    })
    // for each entry in products_unified (for each product_id)
    const newMaxQuantities = {}
    Object.entries(products_unified).map(([id, qty]) => {
      qty = parseInt(qty, 10)
      id = parseInt(id, 10)

      const thisProduct = cartItems.find(ci => ci.product_id === parseInt(id, 10))

      // get the count of previously purchased products for this product_id (stored in redux)
      const numberOfProductsAvailable = maxQuantities[id] ? maxQuantities[id] : 0

      // if max qty limit is enabled for this product, check if the current qty is greater than the limit
      // if so, set the maxQtyProductReached to true for this product
      if (thisProduct && Object.keys(maxQuantities).includes(`${thisProduct.product_id}`)) {
        if (qty > numberOfProductsAvailable) {
          newMaxQuantities[id] = true
        } else {
          newMaxQuantities[id] = false
        }
      }
    })
    setMaxQtyProductReached(newMaxQuantities)
  }

  return checkout?.cart_loading ? (
    <ContentLoader />
  ) : (
    cartItems.map((cartItem, index) => (
      <CartItemCard
        key={cartItem.cart_item_id}
        index={index}
        cartItem={cartItem}
        maxQtyProductReached={maxQtyProductReached}
        currentLocation={currentLocation}
        portal={portal}
        customerGroup={customerGroup}
        cart_items_loading={cart_items_loading}
        maxQuantities={maxQuantities}
        errors={errors}
        youPay={youPay}
        checkout={checkout}
        userCredits={userCredits}
        maxQuantityLoading={maxQuantityLoading}
        currentUser={currentUser}
        mini={props.mini}
        isCheckout={props.isCheckout}
        hideMultiAddressDisplay={props.hideMultiAddressDisplay}
        groupDestinationAddress={props.groupDestinationAddress}
        {...{ queryLoading, setQueryLoading, query, setQuery }}
        {...{ cartItemQuantities, setCartItemQuantities }}
        {...{ tooltipOpen, setTooltipOpen, setDeleteConfirmationItem }}
      />
    ))
  )
}

export default CartItemsList
